import { Component, computed, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { RouterLink } from '@angular/router';
import { FEATURED_PAGE_SIZE, FEATURED_SLUGS } from '../../core/constants/home-slider';
import { CatalogProduct } from '../../core/models/catalog.model';
import { CartService } from '../../core/services/cart.service';
import { CATALOG_PAGE_SIZE_MAX, CatalogService } from '../../core/services/catalog.service';
import { OverlayService } from '../../core/services/overlay.service';
import { HomeSliderComponent } from '../../shared/home-slider/home-slider.component';
import { ProductCardComponent } from '../../shared/product-card/product-card.component';

@Component({
  selector: 'app-home',
  imports: [RouterLink, ProductCardComponent, HomeSliderComponent],
  templateUrl: './home.component.html'
})
export class HomeComponent {
  private readonly cart = inject(CartService);
  private readonly overlay = inject(OverlayService);
  private readonly catalog = inject(CatalogService);

  readonly featuredPage = signal(0);
  readonly featuredHover = signal(false);
  readonly products = signal<CatalogProduct[]>([]);

  readonly featuredAll = computed(() => {
    const bySlug = new Map(this.products().map((item) => [item.slug, item]));
    const ordered = FEATURED_SLUGS.map((slug) => bySlug.get(slug)).filter(
      (item): item is CatalogProduct => !!item
    );
    const rest = this.products().filter((item) => !FEATURED_SLUGS.includes(item.slug));
    return [...ordered, ...rest];
  });

  readonly featured = computed(() => {
    const start = this.featuredPage() * FEATURED_PAGE_SIZE;
    return this.featuredAll().slice(start, start + FEATURED_PAGE_SIZE);
  });

  readonly featuredHasPrev = computed(() => this.featuredPage() > 0);
  readonly featuredHasNext = computed(
    () => (this.featuredPage() + 1) * FEATURED_PAGE_SIZE < this.featuredAll().length
  );

  constructor() {
    this.catalog
      .queryProducts({ page: 1, pageSize: CATALOG_PAGE_SIZE_MAX })
      .pipe(takeUntilDestroyed())
      .subscribe((page) => this.products.set(page.items));
  }

  add(product: CatalogProduct): void {
    this.cart.add(product);
    this.overlay.openCart();
  }

  featuredPrev(event: Event): void {
    event.preventDefault();
    if (!this.featuredHasPrev()) {
      return;
    }
    this.featuredPage.update((page) => page - 1);
  }

  featuredNext(event: Event): void {
    event.preventDefault();
    if (!this.featuredHasNext()) {
      return;
    }
    this.featuredPage.update((page) => page + 1);
  }
}
